/**
 * CookieConsent — TTDSG §25 / DSGVO-konformer Consent-Banner.
 * - Granular (notwendig immer aktiv, Analyse & Marketing optional)
 * - "Ablehnen" gleichwertig zu "Akzeptieren"
 * - Nachweis wird im Backend protokolliert (POST /privacy/cookie-consent)
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, ShieldCheck } from "lucide-react";

const KEY = "tw_cookie_consent";
const VERSION = "2026-06-25";
const BASE = process.env.REACT_APP_BACKEND_URL;

export default function CookieConsent() {
    const [open, setOpen] = useState(false);
    const [settings, setSettings] = useState(false);
    const [analytics, setAnalytics] = useState(false);
    const [marketing, setMarketing] = useState(false);

    useEffect(() => {
        try {
            const saved = JSON.parse(localStorage.getItem(KEY) || "null");
            if (!saved || saved.version !== VERSION) setOpen(true);
        } catch {
            setOpen(true);
        }
    }, []);

    // Jul-2026 (User-Meldung): Banner verdeckte untere Seitenbereiche (z. B.
    // Buchhaltung) — solange offen, bekommt der Seiteninhalt Platz nach unten.
    useEffect(() => {
        if (!open) return undefined;
        document.body.style.paddingBottom = "240px";
        return () => { document.body.style.paddingBottom = ""; };
    }, [open]);

    const persist = async (a, m) => {
        const choice = { version: VERSION, necessary: true, analytics: a, marketing: m, ts: new Date().toISOString() };
        localStorage.setItem(KEY, JSON.stringify(choice));
        setOpen(false);
        try {
            await fetch(`${BASE}/api/privacy/cookie-consent`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ necessary: true, analytics: a, marketing: m, version: VERSION }),
            });
        } catch { /* Nachweis best-effort */ }
    };

    if (!open) return null;

    return (
        <div className="fixed inset-x-0 bottom-0 z-40 p-3 sm:p-4 pointer-events-none" data-testid="cookie-consent">
            <div className="pointer-events-none sm:mr-24">
            <div className="pointer-events-auto mx-auto max-w-3xl rounded-xl border border-gray-200 bg-white p-5 shadow-2xl">
                <div className="flex items-start gap-3">
                    <Cookie className="mt-0.5 h-6 w-6 flex-shrink-0 text-amber-500" />
                    <div className="flex-1">
                        <h2 className="text-base font-bold text-gray-900">Datenschutz-Einstellungen</h2>
                        <p className="mt-1 text-sm text-gray-600">
                            Wir verwenden notwendige Cookies für den Betrieb der Plattform. Optionale Cookies für
                            Analyse und Marketing setzen wir nur mit Ihrer Einwilligung. Sie können diese jederzeit
                            in den Einstellungen widerrufen. Mehr in unserer{" "}
                            <Link to="/datenschutz" className="font-semibold text-gray-900 underline" data-testid="cookie-consent-privacy-link">Datenschutzerklärung</Link>.
                        </p>

                        {settings && (
                            <div className="mt-3 space-y-2" data-testid="cookie-consent-settings">
                                <label className="flex items-center justify-between rounded-lg bg-gray-50 px-3 py-2 text-sm">
                                    <span className="flex items-center gap-2 text-gray-700"><ShieldCheck className="h-4 w-4 text-emerald-500" /> Notwendig (immer aktiv)</span>
                                    <input type="checkbox" checked disabled className="h-4 w-4" />
                                </label>
                                <label className="flex items-center justify-between rounded-lg bg-gray-50 px-3 py-2 text-sm cursor-pointer">
                                    <span className="text-gray-700">Analyse / Statistik</span>
                                    <input type="checkbox" checked={analytics} onChange={(e) => setAnalytics(e.target.checked)} className="h-4 w-4" data-testid="cookie-consent-analytics" />
                                </label>
                                <label className="flex items-center justify-between rounded-lg bg-gray-50 px-3 py-2 text-sm cursor-pointer">
                                    <span className="text-gray-700">Marketing</span>
                                    <input type="checkbox" checked={marketing} onChange={(e) => setMarketing(e.target.checked)} className="h-4 w-4" data-testid="cookie-consent-marketing" />
                                </label>
                            </div>
                        )}

                        <div className="mt-4 flex flex-wrap items-center gap-2">
                            <button onClick={() => persist(false, false)} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-bold text-gray-700 hover:bg-gray-50" data-testid="cookie-consent-reject">
                                Nur notwendige
                            </button>
                            {settings ? (
                                <button onClick={() => persist(analytics, marketing)} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-bold text-gray-700 hover:bg-gray-50" data-testid="cookie-consent-save">
                                    Auswahl speichern
                                </button>
                            ) : (
                                <button onClick={() => setSettings(true)} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-bold text-gray-700 hover:bg-gray-50" data-testid="cookie-consent-settings-btn">
                                    Einstellungen
                                </button>
                            )}
                            <button onClick={() => persist(true, true)} className="rounded-lg bg-emerald-700 px-4 py-2 text-sm font-bold text-white hover:bg-emerald-800" data-testid="cookie-consent-accept">
                                Alle akzeptieren
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            </div>
        </div>
    );
}
